import type { DevicePowerHealth } from './types';

type BatteryManagerLike = EventTarget & {
  level: number;
  charging?: boolean;
  addEventListener(
    type: 'levelchange' | 'chargingchange',
    listener: () => void,
  ): void;
  removeEventListener(
    type: 'levelchange' | 'chargingchange',
    listener: () => void,
  ): void;
};

type NavigatorWithBattery = Navigator & {
  getBattery?: () => Promise<BatteryManagerLike>;
};

function readLevel(battery: BatteryManagerLike): number | null {
  if (typeof battery.level !== 'number' || !Number.isFinite(battery.level)) {
    return null;
  }
  return Math.min(1, Math.max(0, battery.level));
}

/**
 * Battery Status API — Chromium desktop/Android. Unavailable on Safari/iOS/Firefox.
 * When unavailable we return null battery and never block on LOW_BATTERY.
 */
export async function getPowerHealth(): Promise<DevicePowerHealth> {
  const nav = navigator as NavigatorWithBattery;
  if (typeof nav.getBattery !== 'function') {
    return { lowPowerMode: false, batteryLevel: null };
  }

  try {
    const battery = await nav.getBattery();
    return {
      lowPowerMode: false,
      batteryLevel: readLevel(battery),
    };
  } catch {
    return { lowPowerMode: false, batteryLevel: null };
  }
}

export function subscribePowerHealth(
  listener: (health: DevicePowerHealth) => void,
): { remove: () => void } {
  const nav = navigator as NavigatorWithBattery;
  if (typeof nav.getBattery !== 'function') {
    return { remove: () => undefined };
  }

  let cancelled = false;
  let battery: BatteryManagerLike | null = null;

  const emit = () => {
    if (!battery || cancelled) return;
    listener({
      lowPowerMode: false,
      batteryLevel: readLevel(battery),
    });
  };

  void nav
    .getBattery()
    .then(mgr => {
      if (cancelled) return;
      battery = mgr;
      emit();
      mgr.addEventListener('levelchange', emit);
      mgr.addEventListener('chargingchange', emit);
    })
    .catch(() => undefined);

  return {
    remove: () => {
      cancelled = true;
      if (!battery) return;
      battery.removeEventListener('levelchange', emit);
      battery.removeEventListener('chargingchange', emit);
    },
  };
}

/** Web has no thermal API comparable to iOS/Android ProcessInfo / PowerManager. */
export async function getThermalState(): Promise<'unknown'> {
  return 'unknown';
}

export function subscribeThermalState(
  _listener: (state: 'unknown') => void,
): { remove: () => void } {
  return { remove: () => undefined };
}
